import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import type { SchoolInfo } from "@/types";
import type { UnpaidRow } from "@/services/reminderService";
import { drawImageContain, loadPdfImage, pdfDate, pdfMoney } from "./pdfImages";

const PER_PAGE = 3;
const PAGE_W = 210;
const BLOCK_H = 297 / PER_PAGE;
const MARGIN = 14;
/** Au-delà, les derniers frais sont regroupés sur une ligne pour que l'avis tienne dans son tiers de page. */
const MAX_LINES = 5;

const GREEN: [number, number, number] = [29, 95, 208];
const RED: [number, number, number] = [176, 56, 40];
const TEXT: [number, number, number] = [40, 40, 38];
const MUTED: [number, number, number] = [115, 115, 110];

/** Trait de découpe pointillé entre deux avis, avec la mention « découper ici ». */
function cutLine(doc: jsPDF, y: number): void {
  doc.setDrawColor(120, 120, 115);
  doc.setLineWidth(0.35);
  doc.setLineDashPattern([2.5, 1.5], 0);
  doc.line(4, y, PAGE_W - 4, y);
  doc.setLineDashPattern([], 0);
  doc.setFillColor(255, 255, 255);
  doc.rect(PAGE_W / 2 - 15, y - 2, 30, 4, "F");
  doc.setFont("helvetica", "italic");
  doc.setFontSize(7);
  doc.setTextColor(120, 120, 115);
  doc.text("découper ici", PAGE_W / 2, y + 1, { align: "center" });
}

/**
 * Avis de paiement à remettre aux élèves (3 par page A4, séparés par des traits de découpe) : frais en
 * retard ou restant dus, montant total et date limite de régularisation.
 */
export async function exportPaymentNoticesPdf(
  rows: UnpaidRow[],
  school: SchoolInfo,
  deadline: string,
  fileName: string,
): Promise<void> {
  const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
  const logo = await loadPdfImage(school.logo);
  const today = new Date().toISOString().slice(0, 10);

  rows.forEach((row, index) => {
    const slot = index % PER_PAGE;
    if (index > 0 && slot === 0) doc.addPage();
    const top = slot * BLOCK_H;
    if (slot > 0) cutLine(doc, top);
    let y = top + 9;

    // En-tête : école à gauche, titre à droite.
    let schoolX = MARGIN;
    if (logo) {
      drawImageContain(doc, logo, MARGIN, y - 2, 13, 13);
      schoolX = MARGIN + 16;
    }
    doc.setFont("helvetica", "bold");
    doc.setFontSize(10.5);
    doc.setTextColor(...GREEN);
    doc.text(doc.splitTextToSize(school.name.toUpperCase(), 95)[0] as string, schoolX, y + 2);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(7.5);
    doc.setTextColor(...MUTED);
    doc.text([school.city, school.phone].filter(Boolean).join(" · "), schoolX, y + 6.5);

    const late = row.lines.some((l) => l.overdue);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    doc.setTextColor(...(late ? RED : TEXT));
    doc.text(late ? "AVIS DE RETARD DE PAIEMENT" : "AVIS DE PAIEMENT", PAGE_W - MARGIN, y + 2, { align: "right" });
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.setTextColor(...MUTED);
    doc.text(`Le ${pdfDate(today)} — Année ${row.schoolYear}`, PAGE_W - MARGIN, y + 6.5, { align: "right" });

    // Destinataire.
    y += 14;
    doc.setFontSize(9);
    doc.setTextColor(...TEXT);
    doc.setFont("helvetica", "bold");
    doc.text("Élève :", MARGIN, y);
    doc.setFont("helvetica", "normal");
    doc.text(`${row.lastName.toUpperCase()} ${row.firstName}  —  ${row.matricule}  —  ${row.className}`, MARGIN + 13, y);
    doc.setFont("helvetica", "bold");
    doc.text("Parent :", MARGIN, y + 5);
    doc.setFont("helvetica", "normal");
    doc.text(row.guardian ? `${row.guardian.name}  (${row.guardian.phone})` : "—", MARGIN + 13, y + 5);

    // Frais dus.
    const lines = row.lines.slice(0, MAX_LINES);
    const rest = row.lines.slice(MAX_LINES);
    const body = lines.map((l) => [l.label, l.dueDate || "—", pdfMoney(l.remaining)]);
    if (rest.length) {
      body.push([`Autres frais (${rest.length})`, "—", pdfMoney(rest.reduce((acc, l) => acc + l.remaining, 0))]);
    }
    const total = row.lines.reduce((acc, l) => acc + l.remaining, 0);
    autoTable(doc, {
      startY: y + 8,
      margin: { left: MARGIN, right: MARGIN },
      head: [["Frais", "Échéance", "Reste à payer"]],
      body,
      foot: [[late ? "Total à régulariser" : "Total restant dû", "", pdfMoney(total)]],
      theme: "grid",
      styles: { font: "helvetica", fontSize: 8, cellPadding: 1.6, textColor: TEXT, lineColor: [206, 206, 200], lineWidth: 0.2 },
      headStyles: { fillColor: [234, 238, 245], textColor: [55, 60, 55], fontStyle: "bold" },
      footStyles: { fillColor: late ? [250, 232, 228] : [226, 236, 252], textColor: late ? RED : GREEN, fontStyle: "bold" },
      columnStyles: { 1: { cellWidth: 62 }, 2: { cellWidth: 36, halign: "right" } },
      pageBreak: "avoid",
    });

    // Message et signature, en bas du tiers de page.
    const bottom = top + BLOCK_H;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8.5);
    doc.setTextColor(...TEXT);
    const message = doc.splitTextToSize(
      `Nous vous prions de bien vouloir régulariser cette situation avant le ${pdfDate(deadline)}, ` +
        "auprès de la caisse de l'école. Si le paiement a déjà été effectué, merci de ne pas tenir compte de cet avis.",
      PAGE_W - MARGIN * 2 - 45,
    );
    doc.text(message, MARGIN, bottom - 15);
    doc.setFont("helvetica", "bold");
    doc.text(school.signatoryTitle || "La Direction", PAGE_W - MARGIN, bottom - 15, { align: "right" });
  });

  doc.save(fileName);
}
